/**
 * Kurv-regler — spec §4.4 og §4.7.
 *
 * - Antal rundes OP til et multiplum af pack_size og er mindst min_order_qty.
 * - Ordretotaler: subtotal ex moms − cashback + fragt ex moms, + 25 % moms.
 * - Split-levering: opdel linjer i lagervarer vs. restordre.
 *
 * Ren logik — ingen React/Supabase/I/O.
 */

import { VAT_RATE, roundToOre } from "./vat";

export interface QuantityRules {
  packSize?: number;
  minOrderQty?: number;
}

/** Heltal ≥ 1 (ugyldige/manglende værdier → 1). */
function sanitizeRule(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 1) return 1;
  return Math.floor(value);
}

/**
 * Normalisér et ønsket antal: rund OP til nærmeste multiplum af packSize,
 * håndhæv minOrderQty. Ugyldigt/ikke-positivt ønske behandles som 1.
 * Resultatet er altid et multiplum af packSize og ≥ minOrderQty.
 */
export function normalizeQuantity(requested: number, rules: QuantityRules = {}): number {
  const packSize = sanitizeRule(rules.packSize);
  const minOrderQty = sanitizeRule(rules.minOrderQty);
  const req =
    typeof requested === "number" && Number.isFinite(requested) && requested > 0
      ? Math.ceil(requested)
      : 1;
  return Math.ceil(Math.max(req, minOrderQty) / packSize) * packSize;
}

/** Standardantal ved "læg i kurv": max(packSize, minOrderQty), normaliseret. */
export function defaultAddQuantity(rules: QuantityRules = {}): number {
  const packSize = sanitizeRule(rules.packSize);
  const minOrderQty = sanitizeRule(rules.minOrderQty);
  return normalizeQuantity(Math.max(packSize, minOrderQty), rules);
}

/** Ved klik på "+" for en eksisterende linje lægges én packSize til. */
export function incrementQuantity(current: number, rules: QuantityRules = {}): number {
  const packSize = sanitizeRule(rules.packSize);
  return normalizeQuantity(normalizeQuantity(current, rules) + packSize, rules);
}

export interface CartLine {
  variantId: string;
  /** Effektiv B2B-pris ex moms pr. stk. */
  priceExVat: number;
  quantity: number;
  isAvailable?: boolean | null;
  inventoryQuantity?: number | null;
}

/** Linjetotal ex moms (ugyldig/negativ pris eller antal → 0). */
export function lineTotalExVat(line: Pick<CartLine, "priceExVat" | "quantity">): number {
  const price =
    typeof line.priceExVat === "number" && Number.isFinite(line.priceExVat) && line.priceExVat > 0
      ? line.priceExVat
      : 0;
  const qty =
    typeof line.quantity === "number" && Number.isFinite(line.quantity) && line.quantity > 0
      ? line.quantity
      : 0;
  return price * qty;
}

export interface CartTotalsOptions {
  /** Ønsket cashback-forbrug (DKK, 1 point = 1 DKK). Kappes til subtotal. */
  cashbackToUse?: number;
  /** Fragt ex moms. */
  shippingExVat?: number;
}

export interface CartTotals {
  /** Σ linjetotaler ex moms. */
  subtotalExVat: number;
  /** Faktisk anvendt cashback (≥ 0, ≤ subtotal). */
  cashbackApplied: number;
  /** Fragt ex moms (≥ 0). */
  shippingExVat: number;
  /** subtotal − cashback + fragt (ex moms). */
  totalExVat: number;
  /** 25 % af totalExVat. */
  vatAmount: number;
  /** totalExVat + moms — svarer til portal_orders.total_amount. */
  totalInclVat: number;
}

/**
 * Ordretotaler efter spec §4.4:
 *   subtotal_ex = Σ pris × antal
 *   after_cashback = subtotal_ex − cashback
 *   with_shipping = after_cashback + fragt_ex
 *   moms = with_shipping × 0,25
 *   total_incl = with_shipping + moms
 * Alle beløb afrundes til øre (2 decimaler, half up).
 * NB: Kapning mod gyldig cashback-saldo sker i cashback.capCashbackSpend —
 * her kappes kun mod subtotal (og ≥ 0).
 */
export function calculateCartTotals(
  lines: readonly Pick<CartLine, "priceExVat" | "quantity">[],
  opts: CartTotalsOptions = {},
): CartTotals {
  const subtotal = lines.reduce((sum, line) => sum + lineTotalExVat(line), 0);

  const requestedCashback =
    typeof opts.cashbackToUse === "number" &&
    Number.isFinite(opts.cashbackToUse) &&
    opts.cashbackToUse > 0
      ? opts.cashbackToUse
      : 0;
  const cashbackApplied = Math.min(requestedCashback, subtotal);

  const shipping =
    typeof opts.shippingExVat === "number" &&
    Number.isFinite(opts.shippingExVat) &&
    opts.shippingExVat > 0
      ? opts.shippingExVat
      : 0;

  const totalExVat = subtotal - cashbackApplied + shipping;
  const vat = totalExVat * VAT_RATE;

  return {
    subtotalExVat: roundToOre(subtotal),
    cashbackApplied: roundToOre(cashbackApplied),
    shippingExVat: roundToOre(shipping),
    totalExVat: roundToOre(totalExVat),
    vatAmount: roundToOre(vat),
    totalInclVat: roundToOre(totalExVat + vat),
  };
}

/**
 * Er linjen på lager? Eksplicit isAvailable vinder (false ⇒ restordre);
 * ellers inventoryQuantity > 0; mangler begge ⇒ på lager (spec: kun
 * isAvailable === false regnes som restordre).
 */
export function isLineInStock(
  line: Pick<CartLine, "isAvailable" | "inventoryQuantity">,
): boolean {
  if (line.isAvailable === false) return false;
  if (line.isAvailable === true) return true;
  if (typeof line.inventoryQuantity === "number") return line.inventoryQuantity > 0;
  return true;
}

export interface SplitDeliveryPartition<T> {
  inStock: T[];
  backorder: T[];
  /** true når begge grupper er ikke-tomme — ellers giver split ingen mening. */
  shouldSplit: boolean;
}

/** Opdel kurvlinjer i lagervarer vs. restordre (split-levering, spec §5.6). */
export function partitionForSplitDelivery<
  T extends Pick<CartLine, "isAvailable" | "inventoryQuantity">,
>(lines: readonly T[]): SplitDeliveryPartition<T> {
  const inStock: T[] = [];
  const backorder: T[] = [];
  for (const line of lines) {
    (isLineInStock(line) ? inStock : backorder).push(line);
  }
  return {
    inStock,
    backorder,
    shouldSplit: inStock.length > 0 && backorder.length > 0,
  };
}
